import React, { useCallback, useMemo } from "react";
import { Box, Button, Dialog, DialogContent, Typography } from "@mui/material";
import { useUsers } from "../../../../components/Common/HelperComponents/GetDataFromStore";
import { listAuditEvents } from "../../../../helpers/auditHub_helper";
import ActivityTimeline from "./ActivityTimeline";
import { AH_ACCENT, AH_MUTED, AH_WHITE, auditHubPageSx } from "../theme";
import { formatUserName } from "../utils";

const ActivityTrailDialog = ({ audit, open, onClose }) => {
  const { users } = useUsers();

  const userNames = useMemo(() => {
    const map = new Map();
    (Array.isArray(users) ? users : []).forEach((user) => {
      if (user?.email) map.set(String(user.email).toLowerCase(), formatUserName(user));
    });
    return map;
  }, [users]);

  const nameFor = useCallback(
    (email) => userNames.get(String(email || "").toLowerCase()) || email || "",
    [userNames]
  );

  const auditId = audit?.auditId;
  const fetchPage = useCallback((params) => listAuditEvents(auditId, params), [auditId]);
  const label = [audit?.auditCode, audit?.name].filter(Boolean).join(" · ");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="audit-activity-trail-title"
      PaperProps={{ sx: { ...auditHubPageSx, borderRadius: "12px", overflow: "hidden", maxWidth: 720 } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          backgroundColor: AH_ACCENT,
          flexShrink: 0,
        }}
      >
        <Typography id="audit-activity-trail-title" sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}>
          Activity Trail
        </Typography>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          onClick={onClose}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            cursor: "pointer",
          }}
        >
          ×
        </Box>
      </Box>
      <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
        {label ? (
          <Typography sx={{ fontSize: "0.8125rem", color: AH_MUTED, mb: 2, overflowWrap: "anywhere" }}>{label}</Typography>
        ) : null}
        {open && auditId ? (
          <ActivityTimeline
            fetchPage={fetchPage}
            resetKey={auditId}
            nameFor={nameFor}
            showControl
            emptyText="No activity recorded for this assignment yet."
          />
        ) : null}
      </DialogContent>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#f8fafc",
          flexShrink: 0,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            minWidth: 140,
            height: 38,
            px: 2,
            borderRadius: "4px",
            textTransform: "none",
            fontWeight: 400,
            fontSize: "0.875rem",
            boxShadow: "none",
            backgroundColor: "#74788d",
            color: "#eff2f7",
            "&:hover": { backgroundColor: "#636678", boxShadow: "none" },
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

export default ActivityTrailDialog;
